import React, { Fragment, useState, useContext, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import { Formik } from 'formik';
import isArray from 'lodash/isArray';
import moment from 'moment';
import * as Yup from 'yup';
import clsx from 'clsx';
import { GlobalContext } from 'context/GlobalProvider';
import { docTypes } from '../ReservationPage';
import RenderSubmissionForm from './RenderSubmissionForm';
import ResultDialog from 'components/UI/ResultDialog/ResultDialog';
import ButtonWithAsync from 'components/UI/ButtonWithAsync/ButtonWithAsync';
import Button from 'components/UI/Antd/Button/Button';
import { cleanData } from 'library/helpers/papUtils';
import { dateFormat } from 'library/helpers/validators/fieldFormats';
import {
  SubmissionWrapper,
  SubmissionTopSection,
  SubmissionBottomSection,
  SuccessDialogActions
} from './Submission.style';
import configForFE from 'common-pickapier/configForFE';
import { getFormattedReservationEvent } from 'library/helpers/amplitude';
import useAmplitude from 'library/hooks/useAmplitude';
import { generateID, isUnverifiedMarina } from 'library/helpers/utils';
import { useSessionStorage } from 'library/hooks/useSessionStorage';
import { NatureOfTravel } from 'settings/enums';

const AsyncButton = ButtonWithAsync(Button);

const maxDate = moment();
const nameRexp = /^[a-zA-Z][^{0-9}#&@<=>+_"(|)\\\/ˆ˜'*~.,;:$^%±§!?\[\]]+$/;
const phoneRexp = /^(\+{0,1}\d(\d|-){4,})$/;

export const groups = {
  personal_info: {
    title: 'PERSONAL_INFO',
    fields: [
      'first_name',
      'last_name',
      'email_address',
      'date_of_birth',
      'country_of_residence',
      'phone',
      'address',
      'address_line_two',
      'postal_code'
    ]
  },
  vessel_info: {
    title: 'VESSEL_INFO',
    fields: [
      'vessel_name',
      'vessel_type',
      'vessel_hull_type',
      'vessel_loa',
      'vessel_beam',
      'vessel_draft',
      'home_marina_name',
      'water_electricity',
    ]
  },
  documents: {
    title: 'DOCUMENTS',
    fields: ['vessel_insurance', 'vessel_license', 'skipper_id', 'vessel_photo']
  },
  trip_info:{
    title:'TRIP_INFO',
    fields:[
      'private_or_commercial',
      'live_aboard'
    ]
  }
};

const getDocumentsSchemaObject = i18n => ({
  vessel_license: Yup.object()
    .required(i18n.t('DOCUMENT_REQUIRED')),
  vessel_insurance: Yup.object()
    .required(i18n.t('DOCUMENT_REQUIRED')),
    vessel_photo: Yup.object()
    .required(i18n.t('DOCUMENT_REQUIRED')),
  skipper_id: Yup.object()
    .required(i18n.t('DOCUMENT_REQUIRED'))
});

const getSubmissionValuesSchemaObject = (i18n, overrides = {}) => ({
  date_of_birth: Yup.date()
    .nullable()
    .max(
      maxDate,
      i18n.t('DATE_OF_BIRTH_SHOULD_NOT_BE_LATER', {
        maxDate: `${moment(maxDate).format('DD/MM/YYYY')}`
      })
    )
    .required(i18n.t('DATE_OF_BIRTH_REQUIRED')),
  first_name: Yup.string()
    .matches(nameRexp, i18n.t('FIRST_NAME_INCORRECT'))
    .required(i18n.t('FIRST_NAME_REQUIRED')),
  last_name: Yup.string()
    .matches(nameRexp, i18n.t('LAST_NAME_INCORRECT'))
    .required(i18n.t('LAST_NAME_REQUIRED')),
  country_of_residence: Yup.string().required(i18n.t('COUNTRY_REQUIRED')),
  phone: Yup.string()
    .matches(phoneRexp, i18n.t('PHONE_NUMBER_ERROR'))
    .required(i18n.t('PHONE_NUMBER_REQUIRED')),
  address: Yup.string().required(i18n.t('ADDRESS_REQUIRED')),
  address_line_two: Yup.string(),
  postal_code: Yup.string().required(i18n.t('ZIP_CODE_REQUIRED')),
  home_marina_name: Yup.string().required(i18n.t('HOME_MARINA_REQUIRED')),
  vessel_name: Yup.string()
    .trim()
    .required(i18n.t('VESSEL_NAME_REQUIRED'))
    .min(3, i18n.t('VESSEL_NAME_MIN_LETTERS')),
  vessel_type: Yup.string()
    .oneOf(configForFE.VESSEL_TYPES)
    .required(i18n.t('VESSEL_TYPE_REQUIRED')),
  vessel_hull_type: Yup.string()
    .oneOf(configForFE.VESSEL_HULL_TYPES)
    .required(i18n.t('VESSEL_HULL_TYPE_REQUIRED')),
  vessel_loa: Yup.number()
    .typeError(i18n.t('NUMBER_TYPE_ERROR'))
    .min(0.1, i18n.t('VESSEL_LENGTH_ERROR'))
    .max(
        overrides.loa ?? configForFE.MAX_RESERVATION_VESSEL_LOA,
        i18n.t('VESSEL_MAX_LENGTH_ERROR',
        {max_size: overrides.loa ?? configForFE.MAX_RESERVATION_VESSEL_LOA}))
    .required(i18n.t('VESSEL_LOA_REQUIRED')),
  vessel_draft: Yup.number()
    .typeError(i18n.t('NUMBER_TYPE_ERROR'))
    .min(0.1, i18n.t('VESSEL_DRAFT_ERROR'))
    .max(overrides.draft ?? configForFE.MAX_RESERVATION_VESSEL_DRAFT,
        i18n.t('VESSEL_MAX_DRAFT_ERROR',
        {max_size: overrides.draft ?? configForFE.MAX_RESERVATION_VESSEL_DRAFT}))
    .required(i18n.t('VESSEL_DRAFT_REQUIRED')),
  vessel_beam: Yup.number()
    .typeError(i18n.t('NUMBER_TYPE_ERROR'))
    .min(0.1, i18n.t('VESSEL_BEAM_ERROR'))
    .max(overrides.beam ?? configForFE.MAX_RESERVATION_VESSEL_BEAM,
        i18n.t('VESSEL_MAX_BEAM_ERROR',
        {max_size: overrides.beam ?? configForFE.MAX_RESERVATION_VESSEL_BEAM}))
    .required(i18n.t('VESSEL_BEAM_REQUIRED'))
});

const getSubmissionValuesSchemaObjectWithDocuments = i18n => ({
  ...getDocumentsSchemaObject(i18n),
  first_name: Yup.string()
    .matches(nameRexp, i18n.t('FIRST_NAME_INCORRECT'))
    .required(i18n.t('FIRST_NAME_REQUIRED')),
  last_name: Yup.string()
    .matches(nameRexp, i18n.t('LAST_NAME_INCORRECT'))
    .required(i18n.t('LAST_NAME_REQUIRED')),
  date_of_birth: Yup.date()
    .nullable()
    .max(
      maxDate,
      i18n.t('DATE_OF_BIRTH_SHOULD_NOT_BE_LATER', {
        maxDate: `${moment(maxDate).format('DD/MM/YYYY')}`
      })
    )
    .required(i18n.t('DATE_OF_BIRTH_REQUIRED')),
  country_of_residence: Yup.string().required(i18n.t('COUNTRY_REQUIRED')),
  phone: Yup.string()
    .matches(phoneRexp, i18n.t('PHONE_NUMBER_ERROR'))
    .required(i18n.t('PHONE_NUMBER_REQUIRED')),
  address: Yup.string().required(i18n.t('ADDRESS_REQUIRED')),
  postal_code: Yup.string().required(i18n.t('ZIP_CODE_REQUIRED')),
  home_marina_name: Yup.string().required(i18n.t('HOME_MARINA_REQUIRED')),
  vessel_name: Yup.string()
    .trim()
    .required(i18n.t('VESSEL_NAME_REQUIRED'))
    .min(3, i18n.t('VESSEL_NAME_MIN_LETTERS')),
  vessel_type: Yup.string()
    .oneOf(configForFE.VESSEL_TYPES)
    .required(i18n.t('VESSEL_TYPE_REQUIRED')),
  vessel_hull_type: Yup.string()
    .oneOf(configForFE.VESSEL_HULL_TYPES)
    .required(i18n.t('VESSEL_HULL_TYPE_REQUIRED')),
  vessel_loa: Yup.number()
    .typeError(i18n.t('NUMBER_TYPE_ERROR'))
    .min(0.1, i18n.t('VESSEL_LENGTH_ERROR'))
    .max(configForFE.MAX_RESERVATION_VESSEL_LOA, i18n.t('VESSEL_MAX_LENGTH_ERROR', {max_size: configForFE.MAX_RESERVATION_VESSEL_LOA}))
    .required(i18n.t('VESSEL_LOA_REQUIRED')),
  vessel_draft: Yup.number()
    .typeError(i18n.t('NUMBER_TYPE_ERROR'))
    .min(0.1, i18n.t('VESSEL_DRAFT_ERROR'))
    .max(configForFE.MAX_RESERVATION_VESSEL_DRAFT, i18n.t('VESSEL_MAX_DRAFT_ERROR', {max_size: configForFE.MAX_RESERVATION_VESSEL_DRAFT}))
    .required(i18n.t('VESSEL_DRAFT_REQUIRED')),
  vessel_beam: Yup.number()
    .typeError(i18n.t('NUMBER_TYPE_ERROR'))
    .min(0.1, i18n.t('VESSEL_BEAM_ERROR'))
    .max(configForFE.MAX_RESERVATION_VESSEL_BEAM, i18n.t('VESSEL_MAX_BEAM_ERROR', {max_size: configForFE.MAX_RESERVATION_VESSEL_BEAM}))
    .required(i18n.t('VESSEL_BEAM_REQUIRED')),
    private_or_commercial : Yup.string()
    .oneOf(Object.keys(NatureOfTravel))
   .required(i18n.t('NATURE_OF_TRAVEL_REQUIRED'))
});

const Submission = ({
  t,
  i18n,
  vessel_id,
  marina,
  user,
  refreshData,
  reservation,
  status_code,
  location,
  inCollapse,
  personalEmptyGroup,
  vesselEmptyGroup,
  documentsEmptyGroup,
  tripInfo,
  requirementsFormRef,
  filledRequirementsFormRef,
  updateReservation,
  reservation_id,
  submitRequiredFieldsCB
}) => {
  getDocumentsSchemaObject(i18n);
  const reservationLimits = marina.marina_settings?.reservation_limits[reservation.contract_id? "long" : "short"];
  const submissionValuesSchemaObject = getSubmissionValuesSchemaObject(i18n, {
    loa: reservationLimits?.max_vessel_length,
    draft: reservationLimits?.max_vessel_draft,
    beam: reservationLimits?.max_vessel_beam,
  });
  const submissionValuesSchemaObjectWithDocuments =
    getSubmissionValuesSchemaObjectWithDocuments(i18n);

  // const { t } = useTranslation();
  const inputRef = useRef();
  const { homeMarinas, getSingleMarina } = useContext(GlobalContext);
  const [numberOfMarinasViewed, setNumberOfMarinasViewed] = useSessionStorage(
    'numberOfMarinasViewed',
    []
  );
  const [options, setOptions] = useState(
    homeMarinas.reduce((obj, { home_marina_id, home_marina_name }) => {
      if (home_marina_id) {
        return {
          ...obj,
          [home_marina_id]: home_marina_name
        };
      }

      // generateID
      const id = generateID(home_marina_name);

      return {
        ...obj,
        [id]: home_marina_name
      };
    }, {})
  );

  const { name: marinaName, marina_settings } = marina;
  const { marina_requirements } = marina_settings;
  const marinaRequirements = marina_requirements[reservation.contract_id? "long" : "short"].after_approval;
  const { attachments: reservationAttachments, instant_book } = reservation;
  const {
    date_of_birth,
    country_of_residence,
    phone,
    address,
    address_line_two,
    postal_code,
    vessels,
    first_name,
    last_name,
    email: email_address,
    attachments: boaterAttachments
  } = user;
  const { sendToAmplitude } = useAmplitude();

  const live_aboard = reservation.live_aboard;
  const private_or_commercial = reservation.private_or_commercial;

  const currentVessel = vessels.filter(({ id }) => vessel_id === id)[0];
  const {
    attachments: vesselAttachments,
    name: vessel_name,
    home_marina_name,
    water_electricity,
    home_marina_id,
    home_marina_verified,
    type: vessel_type,
    hull_type: vessel_hull_type,
    beam: vessel_beam,
    draft: vessel_draft,
    length: vessel_loa
  } = currentVessel;

  const personalFilledGroup = groups.personal_info.fields.filter(
    field =>
      marinaRequirements.some(req => {
        /**
         * The address field combined by one requirement from marina - 'address'
         * and has two fields
         */
        const isAddressField =
          req === 'address' &&
          (field === 'address_line_two' || field === 'address');
        return isAddressField || req === field;
      }) && !personalEmptyGroup.some(req => req === field)
  );
  const vesselFilledGroup = groups.vessel_info.fields.filter(
    field =>
      marinaRequirements.some(req => req === field) &&
      !vesselEmptyGroup.some(req => req === field)
  );
  const documentsFilledGroup = groups.documents.fields.filter(
    field =>
      marinaRequirements.some(req => req === field) &&
      !documentsEmptyGroup.some(req => req === field)
  );

  const tripInfoFilledGroup = groups.trip_info.fields.filter(
    field =>
    marinaRequirements.some(req => req === field) &&
      !tripInfo.some(req => req === field)
  )

  const vesselLicenseFile = vesselAttachments.filter(
    a => a.attachment_type === docTypes.vessel_license
  )[0];
  const vesselInsuranceFile = vesselAttachments.filter(
    a => a.attachment_type === docTypes.vessel_insurance
  )[0];
  const skiperIdFile = boaterAttachments.filter(
    a => a.attachment_type === docTypes.skipper_id
  )[0];
  const vesselPhotoFile = vesselAttachments.filter(
    a => a.attachment_type === docTypes.vessel_photo
  )[0];

  const documents = {
    vessel_license: vesselLicenseFile
      ? {
          ...vesselLicenseFile,
        }
      : null,
    vessel_insurance: vesselInsuranceFile
      ? {
          ...vesselInsuranceFile,
        }
      : null,
    skipper_id: skiperIdFile
      ? {
          ...skiperIdFile,
        }
      : null,
      vessel_photo: vesselPhotoFile
        ? {
          ... vesselPhotoFile,
        }
        : null
  };

  // required values initialization
  let requiredValues = {
    first_name: '',
    last_name: '',
    email_address: '',
    date_of_birth: null,
    country_of_residence: '',
    phone: '',
    address: '',
    address_line_two: '',
    postal_code: '',
    vessel_name: '',
    vessel_type: '',
    vessel_hull_type: '',
    vessel_loa: '',
    vessel_beam: '',
    vessel_draft: '',
    home_marina_name: '',
    water_electricity: '',
    vessel_license: documents.vessel_license,
    vessel_insurance: documents.vessel_insurance,
    vessel_photo: documents.vessel_photo,
    skipper_id: documents.skipper_id,
    private_or_commercial:'',
    live_aboard:''
  };
  Object.keys(requiredValues).forEach(key => {
    if (
      !personalEmptyGroup
        .concat(vesselEmptyGroup)
        .concat(documentsEmptyGroup)
        .concat(tripInfo)
        .some(req => req === key)
    ) {
      delete requiredValues[key];
    }
  });
  if (vesselEmptyGroup.some(key => key === 'home_marina_name')) {
    requiredValues = { ...requiredValues, home_marina_id: null };
  }

  // filled values initialization
  let filledValues = {
    first_name,
    last_name,
    email_address,
    date_of_birth: date_of_birth ? moment(date_of_birth) : null,
    country_of_residence,
    phone,
    address,
    address_line_two,
    postal_code,
    vessel_name,
    vessel_type,
    vessel_hull_type,
    vessel_beam,
    vessel_draft,
    vessel_loa,
    home_marina_name,
    water_electricity,
    vessel_license: documents.vessel_license,
    vessel_insurance: documents.vessel_insurance,
    vessel_photo: documents.vessel_photo,
    skipper_id: documents.skipper_id,
    private_or_commercial:private_or_commercial? "COMMERCIAL": "PRIVATE",
    live_aboard:live_aboard
  };

  Object.keys(filledValues).forEach(key => {
    if (
      !personalFilledGroup
        .concat(vesselFilledGroup)
        .concat(documentsFilledGroup)
        .concat(tripInfoFilledGroup)
        .some(req => req === key)
    ) {
      delete filledValues[key];
    }
  });

  const getInitialHomeMarinaId = () => {
    if (home_marina_id) return home_marina_id;
    else if (home_marina_name) return generateID(home_marina_name);

    return null;
  };

  if (vesselFilledGroup.some(key => key === 'home_marina_name')) {
    filledValues = {
      ...filledValues,
      home_marina_id: getInitialHomeMarinaId()
    };
  }

  // states
  const [otherSelected, setOtherSelected] = useState(
    !home_marina_id && home_marina_name
  );
  const [defaultOpen, setDefaultOpen] = useState(false);
  const [selected, setSelected] = useState(home_marina_id);
  const [editMode, setEditMode] = useState(false);
  const [successOpened, setSuccessOpened] = useState(false);
  const [submitedByForm, setSubmitedByForm] = useState(false);

  const isCanceled =
    status_code === 'CANCELED_BY_MARINA' ||
    status_code === 'CANCELED_BY_BOATER' ||
    status_code === 'EXPIRED_NO_MARINA_RESPONSE' ||
    status_code === 'EXPIRED_NO_BOATER_RESPONSE';
  const isApproved = status_code === 'APPROVED';

  const onOtherClick = () => {
    setOtherSelected(true);
  };

  const onSelect = id => {
    setSelected(id);
    setOtherSelected(false);
  };

  const prepareData = formProps => {
    const {
      first_name,
      last_name,
      email_address,
      date_of_birth,
      country_of_residence,
      phone,
      address,
      address_line_two,
      postal_code,
      vessel_name,
      vessel_type,
      vessel_hull_type,
      vessel_loa,
      vessel_beam,
      vessel_draft,
      home_marina_name,
      home_marina_id,
      water_electricity,
      vessel_license,
      vessel_insurance,
      vessel_photo,
      skipper_id,
      private_or_commercial,
      live_aboard
    } = formProps;

    const _isUnverifiedMarina = isUnverifiedMarina(home_marina_id);
    let home_marina = {};
    if (home_marina_id) {
      if (_isUnverifiedMarina) {
        const unverified_marina_name = `${home_marina_id}`.split('_')[1];
        home_marina = {
          home_marina_name: unverified_marina_name
        };
      } else {
        home_marina = { home_marina_id };
      }
    }

    const selectedDate =
      date_of_birth !== undefined
        ? date_of_birth
          ? moment(date_of_birth).format(dateFormat)
          : null
        : undefined;

    const boaterNotRequiredFields = { address_line_two };
    const boater = {
      ...boaterNotRequiredFields,
      ...cleanData({
        first_name,
        last_name,
        email: email_address,
        date_of_birth: selectedDate,
        country_of_residence,
        phone,
        address,
        postal_code
      })
    };
    const vessel = cleanData({
      id: vessel_id,
      name: vessel_name,
      type: vessel_type,
      hull_type: vessel_hull_type,
      beam: vessel_beam,
      draft: vessel_draft,
      length: vessel_loa,
      ...home_marina,
      water_electricity
    });
    const attachment_ids = Object.keys(docTypes)
      .filter(key => !!formProps[key])
      .map(key => formProps[key].id);
    return { boater, vessel, attachment_ids,private_or_commercial,
      live_aboard };
  };

  const onSubmitRequiredHandler = async (formProps, { setErrors }) => {
    if (
      Object.keys(requiredValues).some(key => key === 'home_marina_name') &&
      !options[selected]
    ) {
      setErrors({ home_marina_id: t('MARINA_NOT_SELECTED_ADD') });
      return false;
    }
    const data = prepareData(formProps);
    submitRequiredFieldsCB(data);
  };

  const onSubmitFilledHandler = async formProps => {
    if (!editMode) {
      return false;
    }
    const data = prepareData(formProps);
    const requestData = {
      ...data,
      reservation_id,
      method: 'UPDATE'
    };
    const res = await updateReservation(requestData);
    if (res && !res.error_type) {
      if (submitedByForm) {
        setSuccessOpened(true);
        setSubmitedByForm(false);
      }
      // Amplitude event

      const reservationVessel = user.vessels.filter(
        ({ id }) => id === reservation.vessel_id
      )[0];

      const today = moment().format('YYYY-MM-DD');
      const days_before = moment(reservation.departure_date).diff(
        today,
        'days'
      );

      const eventProperties = getFormattedReservationEvent({
        reservationVessel,
        payment: reservation.payment,
        user,
        data: requestData,
        reservation,
        marina,
        location,
        type: 'updated_fields',
        days_before,
        marinas_viewed_before: numberOfMarinasViewed.length - 1,
        initialValues: filledValues,
        popeye_discount: reservation.popeye_discount,
      });

      sendToAmplitude({
        eventProperties: {...eventProperties, origin: location.state?.origin},
        eventName: 'reservation_updated'
      });
      setNumberOfMarinasViewed([]);
    }
  };

  const onUpdateAttachment = async value => {
    const data = {
      reservation_id,
      method: 'UPDATE',
      attachment_ids: [value.id],
      vessel: { id: vessel_id }
    };
    await updateReservation(data);
    await refreshData({ parameters: ['vessels', 'boater'] });
  };

  const onSuccess = async () => {
    await refreshData({ parameters: ['vessels', 'boater', 'marina_requests'] });
    setSuccessOpened(false);
    setEditMode(false);
  };

  const getvalidationSchema = (values, isFilledForm = false) => {
    if (!values) return null;
    const schemaObject = isFilledForm
      ? { ...submissionValuesSchemaObject }
      : { ...submissionValuesSchemaObjectWithDocuments };
    Object.keys(schemaObject).forEach(key => {
      if (!Object.keys(values).some(req => req === key)) {
        delete schemaObject[key];
      }
    });

    if (Object.keys(values).some(key => key === 'home_marina_name')) {
      // Will be handled on the on submit method
      delete schemaObject['home_marina_name'];
    }

    let schema = Yup.object().shape(schemaObject);

    return schema;
  };

  const onCancel = () => {
    if (!home_marina_id && !home_marina_name) {
      setSelected(null);
      if (otherSelected) {
        setOtherSelected(false);
      }
    } else {
      if (home_marina_name && home_marina_id) {
        setOtherSelected(false);
        setSelected(null);
      } else {
        setOtherSelected(true);
      }
    }
    setSubmitedByForm(false);
    setEditMode(false);
  };

  const handleAddNewHomeMarina = newHomeMarina => {
    const newOptions = { ...options };
    newOptions[`_${newHomeMarina}`] = newHomeMarina;
    setOptions(newOptions);
  };

  return (
    <SubmissionWrapper
      className={clsx({
        'in-collapse': inCollapse,
        finished: isCanceled || isApproved,
        'no-payment': !reservation.payment_accepted_online
      })}
    >
      {(!!personalEmptyGroup.length ||
        !!vesselEmptyGroup.length ||
        !!tripInfo.length ||
        !!documentsEmptyGroup.length) && (
        <Fragment>
          {/* {!instant_book ? (
            <Alert
              showIcon
              type='info'
              message={t('ENTER_REQUIRED_INFO_TO_CONFIRM_RESERVATION')}
            />
          ) : null} */}
          <SubmissionTopSection instantBook={instant_book}>
            <Formik
              // enableReinitialize
              innerRef={requirementsFormRef}
              initialValues={requiredValues}
              onSubmit={onSubmitRequiredHandler}
              validationSchema={getvalidationSchema(requiredValues)}
            >
              {props => (
                <RenderSubmissionForm
                  {...props}
                  isFilledSection={false}
                  personalGroup={personalEmptyGroup}
                  vesselGroup={vesselEmptyGroup}
                  documentsGroup={documentsEmptyGroup}
                  tripInfo={tripInfo}
                  marinaName={marinaName}
                  refreshData={refreshData}
                  home_marina_id={home_marina_id}
                  home_marina_verified={home_marina_verified}
                  home_marina_name={home_marina_name}
                  onSelect={id => {
                    props.setFieldValue('home_marina_name', options[id]);
                    onSelect(id);
                  }}
                  onChange={value => {
                    setSelected(null);
                  }}
                  otherSelected={otherSelected}
                  options={options}
                  defaultOpen={defaultOpen}
                  inputRef={inputRef}
                  editMode={!isCanceled}
                  isCanceled={isCanceled}
                  isApproved={isApproved}
                  vessel_id={vessel_id}
                  onAddNewHomeMarina={handleAddNewHomeMarina}
                />
              )}
            </Formik>
          </SubmissionTopSection>
        </Fragment>
      )}
      {(!!personalFilledGroup.length ||
        !!vesselFilledGroup.length ||
        !!tripInfoFilledGroup ||
        !!documentsFilledGroup.length) && (
        <SubmissionBottomSection>
          <Formik
            enableReinitialize
            innerRef={filledRequirementsFormRef}
            initialValues={filledValues}
            onSubmit={onSubmitFilledHandler}
            validationSchema={getvalidationSchema(filledValues, true)}
          >
            {props => (
              <RenderSubmissionForm
                {...props}
                isFilledSection={true}
                personalGroup={personalFilledGroup}
                vesselGroup={vesselFilledGroup}
                documentsGroup={documentsFilledGroup}
                tripInfo={tripInfoFilledGroup}
                marinaName={marinaName}
                refreshData={refreshData}
                onChangeAttachment={value => onUpdateAttachment(value)}
                home_marina_id={home_marina_id}
                home_marina_verified={home_marina_verified}
                home_marina_name={home_marina_name}
                onOtherClick={() => {
                  onOtherClick();
                  props.setFieldValue('home_marina_id', null);
                }}
                onSelect={id => {
                  const selectedMarina = getSingleMarina(id);
                  props.setFieldValue('home_marina_name', selectedMarina.name);
                  onSelect(id);
                }}
                onChange={value => {
                  setSelected(null);
                }}
                otherSelected={otherSelected}
                options={options}
                defaultOpen={defaultOpen}
                inputRef={inputRef}
                editMode={editMode}
                onEdit={() => setEditMode(true)}
                onCancel={onCancel}
                isCanceled={isCanceled}
                isApproved={isApproved}
                onClickFilledSubmit={() => setSubmitedByForm(true)}
                vessel_id={vessel_id}
              />
            )}
          </Formik>

          <ResultDialog
            className='success-profile-update-result'
            visible={successOpened}
            closable={false}
            title={t('YOUR_PROFILE_IS_UPDATED')}
            actions={
              <SuccessDialogActions>
                <AsyncButton
                  asyncAction={onSuccess}
                  htmlType='button'
                  type='primary'
                  label={t('OK_BUTTON')}
                />
              </SuccessDialogActions>
            }
          />
        </SubmissionBottomSection>
      )}
    </SubmissionWrapper>
  );
};

export default withTranslation()(Submission);
